/**
 * The canonical domain model.
 *
 * Everything downstream of ingest speaks these types, regardless of which site
 * collection a submission originally landed in. Adding a site means writing a
 * mapper to `CanonicalLead`, not touching routing, delivery, or reporting.
 */

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export const QUALITY_LEVELS = ["clean", "suspect", "spam"] as const;
export type QualityLevel = (typeof QUALITY_LEVELS)[number];

export const ROUTING_STATUSES = ["pending", "routed", "held", "skipped"] as const;
export type RoutingStatus = (typeof ROUTING_STATUSES)[number];

/**
 * One qualifying answer from the form — "Looking to: Leasa", "Placement: Kontor".
 *
 * Kept as an open list rather than fixed columns because each site asks slightly
 * different questions, and a question we don't recognise must still reach the
 * partner rather than being silently dropped.
 */
export interface LeadDetail {
  label: string;
  value: string;
}

/** Contact + context fields extracted from a raw site submission. */
export interface LeadFields {
  name: string;
  email: string;
  phone: string;
  company: string;
  city: string;
  state: string;
  postalCode: string;
  message: string;
  /** Free-text "what they want" — machine type, service interest, etc. */
  interest: string;
  /** Everything else the form asked, in display order. */
  details: LeadDetail[];
  sourceUrl: string;
  referrer: string;
  utm: Record<string, string>;
  ip: string;
  userAgent: string;
}

/** A normalized lead, ready to be persisted into the canonical `leads` collection. */
export interface CanonicalLead extends LeadFields {
  /** `${collection}:${recordId}` — the unique ingest key. */
  sourceKey: string;
  sourceCollection: string;
  sourceRecordId: string;
  siteKey: string;
  /** ISO-8601. Taken from the source record's created timestamp. */
  submittedAt: string;
  /** The untouched original record, kept forever for debugging and replay. */
  raw: Record<string, unknown>;
}

/** A lead as stored in PocketBase, including everything the pipeline decided about it. */
export interface LeadRecord extends CanonicalLead {
  id: string;
  quality: QualityLevel;
  qualityReasons: string[];
  isDuplicate: boolean;
  duplicateOf: string;
  routingStatus: RoutingStatus;
  routedAt: string;
  created: string;
}

// ---------------------------------------------------------------------------
// Destinations
// ---------------------------------------------------------------------------

export interface Destination {
  id: string;
  /** Stable slug used in dedupe keys — never change one in place. */
  key: string;
  name: string;
  active: boolean;
  channel: "email";
  /** Where individual lead notifications go. */
  emails: string[];
  ccEmails: string[];
  /** Which sites route here. Empty means every site. */
  siteKeys: string[];
  /**
   * The partnership window, compared against the lead's `submittedAt` — not
   * against ingestion time, so re-ingesting or backfilling never changes who
   * was eligible. `null` means unbounded on that end.
   *
   * Optional on the type only so existing test fixtures stay valid; the mapper
   * always populates both.
   */
  deliverFrom?: Date | null;
  deliverUntil?: Date | null;
  /** Minimum quality required to forward. `clean` never forwards suspect leads. */
  minQuality: Exclude<QualityLevel, "spam">;
  dailyReportEnabled: boolean;
  /** Recipients of the daily summary; falls back to `emails` when empty. */
  dailyReportEmails: string[];
  includeCsvInDaily: boolean;
  notes: string;
}

// ---------------------------------------------------------------------------
// Deliveries (the outbox)
// ---------------------------------------------------------------------------

/**
 * `pending`   — queued, waiting for `nextAttemptAt`
 * `sending`   — claimed by a worker, in flight
 * `sent`      — Resend accepted it
 * `delivered` — recipient's mail server accepted it (via webhook)
 * `bounced`   — permanently rejected (via webhook)
 * `complained`— delivered but marked as spam (via webhook)
 * `dead`      — retries exhausted; needs a human
 */
export const DELIVERY_STATUSES = [
  "pending",
  "sending",
  "sent",
  "delivered",
  "bounced",
  "complained",
  "dead",
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Statuses that mean "we are done trying" — no sweeper should pick these up. */
export const TERMINAL_DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  "sent",
  "delivered",
  "bounced",
  "complained",
  "dead",
];

export interface DeliveryRecord {
  id: string;
  lead: string;
  destination: string;
  /** `${leadId}:${destinationKey}` — unique, so routing can safely run twice. */
  dedupeKey: string;
  channel: "email";
  status: DeliveryStatus;
  attempts: number;
  nextAttemptAt: string;
  lastError: string;
  /** Resend's email id, used to correlate webhook events back to this row. */
  providerMessageId: string;
  idempotencyKey: string;
  recipients: string[];
  sentAt: string;
  deliveredAt: string;
  created: string;
}

// ---------------------------------------------------------------------------
// Daily reports
// ---------------------------------------------------------------------------

export type ReportScope = "partner" | "internal";

export interface DailyReportRecord {
  id: string;
  /** YYYY-MM-DD in the configured report timezone. */
  reportDate: string;
  scope: ReportScope;
  destination: string;
  /** `${reportDate}:${scope}:${destinationKey|internal}` — unique. */
  dedupeKey: string;
  status: "pending" | "sent" | "failed";
  attempts: number;
  stats: Record<string, unknown>;
  html: string;
  lastError: string;
  sentAt: string;
}

// ---------------------------------------------------------------------------
// Site registration
// ---------------------------------------------------------------------------

/**
 * Describes one lead-capture website and how to read its PocketBase collection.
 * Registered in `src/sites.ts`.
 */
export interface SiteDefinition {
  /** Stable slug, e.g. "vendingleads-tx". Used in routing and reports. */
  key: string;
  /** Human-readable name shown to the partner and in reports. */
  name: string;
  /** The PocketBase collection this site writes into. */
  collection: string;
  /**
   * Optional explicit field mapping. Anything omitted falls back to the
   * heuristic mapper, which handles the usual name/email/phone/message aliases.
   */
  map?: Partial<Record<keyof LeadFields, string | string[]>>;
  /**
   * Honeypot field names. A submission with any of these non-empty is a bot.
   */
  honeypotFields?: string[];
  /**
   * Extra label overrides for this site's qualifying columns, on top of the
   * shared table in `src/core/vocabulary.ts`.
   */
  detailLabels?: Record<string, string>;
  /** Columns this site should never forward to a partner. */
  excludeFields?: string[];
  active?: boolean;
}
